/**
 * Pure per-currency bucketing for balance totals, plus the paymentTo /
 * partyType rule that decides what enters a balance saldo — frontend side.
 * Mirrors helpers/balanceTotals.helper.js; keep both in sync:
 * the same rule has to hold on the screen, in the PDF and in the aggregate,
 * and those three compute their totals independently.
 *
 * There is no FX/exchange-rate mechanism anywhere in this app. Currencies
 * are therefore always segregated into their own bucket, never converted or
 * summed together.
 *
 * The rule
 * --------
 * paymentTo === 'Inmobiliaria': the agency collects the income, so it
 *   holds money that isn't its own and owes the propietario
 *       saldo = alquiler − IRPF − comisiones − gastos + cargos inquilino  (a PAGAR)
 *
 * paymentTo === 'Cliente': the propietario collects the income
 *   directly. The agency never touches it, so there is nothing to net the
 *   costs against and the settlement runs the other way
 *       saldo = −(comisiones + gastos) + cargos inquilino                  (a COBRAR)
 *
 * Both are the same signed expression with the rent term present or absent —
 * the sign carries the direction of payment.
 */

const DEFAULT_CURRENCY = 'USD';

/** A due's currency comes from its parent debt (debtModel.currency is required). */
export const resolveDueCurrency = (due) => {
	const currency = due?.debt?.currency || due?.currency;
	return currency ? String(currency).toUpperCase() : undefined;
};

/**
 * Bills store no currency of their own — it's implicitly asset.currency.
 * Returns undefined (not a default) when neither is known, so callers that
 * use this to pre-fill a form field (BillsEditor) leave it for the user to
 * pick rather than silently guessing. bucketTotals below applies its own
 * default when grouping, since every bill still needs a bucket.
 */
export const resolveBillCurrency = (bill) => {
	if (!bill) return undefined;
	if (bill.currency != null && bill.currency !== '') {
		return String(bill.currency).toUpperCase();
	}
	const asset = bill.asset;
	if (asset && typeof asset === 'object' && asset.currency) {
		return String(asset.currency).toUpperCase();
	}
	return undefined;
};

/**
 * Resolves who receives the income for debts linked to either supported source.
 * Contract wins only as a defensive precedence rule; a debt normally belongs
 * to a contract or an asset sale, not both.
 */
export const resolveDebtPaymentTo = (debt) =>
	debt?.contract?.paymentTo ?? debt?.assetSale?.paymentTo ?? null;

/**
 * Whether a due's income is money the agency is actually holding.
 *
 * Tested against the EXCLUDING value on purpose: paymentTo is
 * `required: false`, and direct debts carry no source at all. Everything unset
 * keeps counting exactly as it did before this rule existed, instead of
 * silently dropping out of historical balances.
 */
export const dueIsCollected = (due) => due?.debtPaymentTo !== 'Cliente';

/**
 * Whether a bill is charged to the propietario. Bills marked 'inquilino' are
 * the tenant's to pay and must never reduce the landlord's settlement.
 * Unset (legacy) bills keep charging the owner, as they do today.
 */
export const billChargesOwner = (bill) => bill?.partyType !== 'inquilino';

/** Bills assigned to the tenant are collected by the agency and increase the settlement. */
export const billChargesTenant = (bill) => bill?.partyType === 'inquilino';

const percentageOf = (commission) => {
	if (commission?.percentage === null || commission?.percentage === undefined) return null;
	const percentage = Number(commission.percentage);
	return Number.isNaN(percentage) ? null : percentage;
};

/**
 * Percentage commissions remain dynamic; fixed commissions use their stored
 * amount. This keeps manually-entered commissions in the balance even when no
 * percentage was supplied.
 */
export const computeCommissionDeduction = (commission, duesTotal, irpfDeduction) => {
	const percentage = percentageOf(commission);
	if (percentage !== null) {
		const base = commission.calculationBase === 'compound'
			? duesTotal + irpfDeduction
			: duesTotal;
		return Math.round((base * percentage / 100) * 100) / 100;
	}
	const amount = Number(commission?.amount);
	return Number.isNaN(amount) ? 0 : amount;
};

const resolveFixedCommissionCurrency = (commission, currencies = []) => {
	if (commission?.currency) return String(commission.currency).toUpperCase();
	return currencies.length === 1 ? currencies[0] : DEFAULT_CURRENCY;
};

/**
 * Which bucket a commission is deducted from. A percentage commission is
 * computed against every bucket's own dues base, so it belongs to all of
 * them. A fixed commission is a stored amount in commission.currency and must
 * land in exactly one bucket — otherwise a USD + UYU balance would deduct it
 * twice. `currencies` is the fallback when the commission carries no currency:
 * the only bucket if there is just one, else the default.
 */
export const commissionAppliesToCurrency = (commission, currency, currencies = []) => {
	if (percentageOf(commission) !== null) return true;
	return resolveFixedCommissionCurrency(commission, currencies) === currency;
};

/**
 * The saldo is signed: positive means the agency owes the propietario,
 * negative means the propietario owes the agency. Deriving the wording from
 * the sign — rather than from paymentTo — is what lets an account mixing both
 * collection modes read correctly, and makes an 'Inmobiliaria' contract whose
 * costs exceed its rent come out right on its own.
 */
export const saldoLabel = (signed) => (signed >= 0
	? 'Saldo a pagar al propietario'
	: 'Saldo a cobrar al propietario');

/**
 * Summarises which collection mode(s) a set of income dues represents, so the
 * screen and the PDF can caption the total from one shared derivation instead
 * of reimplementing the rule twice.
 *
 * `expenseConcepts` is the caller's dynamic treatAsExpense set; it defaults to
 * the single hardcoded slug so existing callers keep their old behaviour.
 */
const DEFAULT_EXPENSE_CONCEPTS = new Set(['gasto-de-activo-fijo']);

export const describeBalanceMode = (dues = [], expenseConcepts = DEFAULT_EXPENSE_CONCEPTS) => {
	let collectedCount = 0;
	let directCount = 0;
	dues.forEach((due) => {
		// Expense-flagged concepts are a deduction, not income — they say nothing
		// about who collects the rent, so they must not steer the caption.
		if (expenseConcepts.has(due.debtConcept)) return;
		if (dueIsCollected(due)) collectedCount += 1;
		else directCount += 1;
	});
	let mode = 'none';
	if (collectedCount > 0 && directCount > 0) mode = 'mixed';
	else if (directCount > 0) mode = 'owner';
	else if (collectedCount > 0) mode = 'agency';
	return { mode, collectedCount, directCount };
};

/**
 * Short label naming which side receives the income, from its source paymentTo.
 * Deliberately just the label — the arithmetic is visible in the table rows
 * themselves and in the sign-derived total, so restating a formula here only
 * adds noise.
 */
export const buildBalanceCaption = (dues = [], expenseConcepts = DEFAULT_EXPENSE_CONCEPTS) => {
	const { mode } = describeBalanceMode(dues, expenseConcepts);
	if (mode === 'none') return null;
	if (mode === 'mixed') return 'Pago a inmobiliaria y a propietario';
	return mode === 'owner' ? 'Pago a propietario' : 'Pago a inmobiliaria';
};

const emptyBucket = () => ({
	// All income dues. Stays the commission base and what the table lists —
	// commission is earned on the rent regardless of who collects it.
	debtDuesTotal: 0,
	// Only the dues the agency actually holds. This is what enters the saldo
	// and what IRPF is withheld from.
	collectedDuesTotal: 0,
	irpfDeduction: 0,
	commissionsTotal: 0,
	gastoActivoFijoTotal: 0,
	// Owner (and legacy, unset partyType) bills: reduce the saldo.
	billsTotal: 0,
	// Tenant bills: collected by the agency, so they increase the saldo.
	tenantBillsTotal: 0,
	grandTotal: 0,
});

/**
 * Buckets dues, bills and commissions by currency. Every bucket is a fully
 * self-contained balance total — never converted into or combined with
 * another currency's bucket.
 *
 * @param {Object} params
 * @param {Array} params.dues - enriched due objects (debtConcept, amount, surcharge, debt.currency)
 * @param {Array} params.bills - bill objects (amount, currency|asset.currency)
 * @param {Array} params.commissions - percentage commissions apply against each currency's own dues base; fixed ones only to their own currency
 * @param {Set<string>} params.expenseConcepts
 * @param {number} params.irpf - IRPF percentage (0-100)
 * @returns {{ byCurrency: Object, currencies: string[], mixedCurrency: boolean }}
 */
export const bucketTotals = ({ dues = [], bills = [], commissions = [], expenseConcepts = new Set(), irpf = 0 }) => {
	const byCurrency = {};
	const ensureBucket = (currency) => {
		if (!byCurrency[currency]) byCurrency[currency] = emptyBucket();
		return byCurrency[currency];
	};

	dues.forEach((due) => {
		const bucket = ensureBucket(resolveDueCurrency(due) || DEFAULT_CURRENCY);
		const amount = (due.amount || 0) + (due.surcharge || 0);
		if (expenseConcepts.has(due.debtConcept)) {
			bucket.gastoActivoFijoTotal += amount;
		} else {
			bucket.debtDuesTotal += amount;
			if (dueIsCollected(due)) bucket.collectedDuesTotal += amount;
		}
	});

	bills.forEach((bill) => {
		const bucket = ensureBucket(resolveBillCurrency(bill) || DEFAULT_CURRENCY);
		if (billChargesTenant(bill)) bucket.tenantBillsTotal += bill.amount || 0;
		else bucket.billsTotal += bill.amount || 0;
	});

	// A fixed commission needs a bucket even when nothing else is in its currency.
	const dataCurrencies = Object.keys(byCurrency);
	commissions.forEach((commission) => {
		if (percentageOf(commission) === null) ensureBucket(resolveFixedCommissionCurrency(commission, dataCurrencies));
	});
	const currencies = Object.keys(byCurrency);

	currencies.forEach((currency) => {
		const bucket = byCurrency[currency];
		// IRPF is withheld by whoever collects. No collection, no withholding.
		bucket.irpfDeduction = (bucket.collectedDuesTotal * irpf) / 100;
		// Commission base is ALL rent, not just the collected part — that is the
		// whole point of the 'Cliente' mode: you earn it without holding the money.
		commissions.forEach((commission) => {
			if (!commissionAppliesToCurrency(commission, currency, dataCurrencies)) return;
			bucket.commissionsTotal += computeCommissionDeduction(commission, bucket.debtDuesTotal, bucket.irpfDeduction);
		});
		// Signed saldo: the sign carries the direction of payment. Positive means
		// the agency owes the propietario; negative means the propietario owes the
		// agency (no rent to net the costs against, or costs exceeding the rent).
		bucket.grandTotal = bucket.collectedDuesTotal - bucket.irpfDeduction - bucket.commissionsTotal
			- bucket.gastoActivoFijoTotal - bucket.billsTotal + bucket.tenantBillsTotal;
	});

	return { byCurrency, currencies, mixedCurrency: currencies.length > 1 };
};

/**
 * Runs bucketTotals independently for each asset, given the same per-asset
 * groupings the account-type balance already builds (debtDuesByAsset,
 * billsByAsset, commissionsByAsset).
 */
export const bucketTotalsByAsset = ({ debtDuesByAsset = {}, billsByAsset = {}, commissionsByAsset = {}, expenseConcepts = new Set(), irpf = 0 }) => {
	const assetIds = new Set([
		...Object.keys(debtDuesByAsset),
		...Object.keys(billsByAsset),
		// Fixed commissions count even on an asset with no dues or bills this month.
		...Object.keys(commissionsByAsset),
	]);
	const result = {};
	assetIds.forEach((assetId) => {
		result[assetId] = bucketTotals({
			dues: debtDuesByAsset[assetId] || [],
			bills: billsByAsset[assetId] || [],
			commissions: commissionsByAsset[assetId] || [],
			expenseConcepts,
			irpf,
		});
	});
	return result;
};

/**
 * Sums multiple bucketTotals()-shaped results into one, currency by
 * currency. Used to roll per-asset buckets (plus anything that couldn't be
 * traced to an asset) up into a whole-balance total without ever adding two
 * different currencies together.
 */
export const mergeBucketResults = (results) => {
	const merged = {};
	results.forEach(({ byCurrency }) => {
		Object.entries(byCurrency || {}).forEach(([currency, bucket]) => {
			if (!merged[currency]) merged[currency] = emptyBucket();
			const target = merged[currency];
			target.debtDuesTotal += bucket.debtDuesTotal;
			target.collectedDuesTotal += bucket.collectedDuesTotal;
			target.irpfDeduction += bucket.irpfDeduction;
			target.commissionsTotal += bucket.commissionsTotal;
			target.gastoActivoFijoTotal += bucket.gastoActivoFijoTotal;
			target.billsTotal += bucket.billsTotal;
			target.tenantBillsTotal += bucket.tenantBillsTotal;
			target.grandTotal += bucket.grandTotal;
		});
	});
	const currencies = Object.keys(merged);
	return { byCurrency: merged, currencies, mixedCurrency: currencies.length > 1 };
};
